import { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  IconButton,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Divider,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {
  Print as PrintIcon,
  Close as CloseIcon,
  Clear as ClearIcon
} from '@mui/icons-material'
import dayjs from 'dayjs'
import supabase from '../supabaseClient'
import '../styles/print.css'

// Date range options - moved outside component to prevent re-creation
const dateRangeOptions = [
  { value: 'month', label: 'Past Month', days: 30 },
  { value: '3months', label: 'Past 3 Months', days: 90 },
  { value: '6months', label: 'Past 6 Months', days: 180 },
  { value: 'year', label: 'Past Year', days: 365 },
  { value: 'all', label: 'All Time', days: null },
  { value: 'custom', label: 'Custom Range', days: null }
]

const StudentPrintView = ({ 
  student, 
  classes, 
  guardian, 
  open, 
  onClose 
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  
  const [printOptions, setPrintOptions] = useState({
    includeAttendance: true,
    includeQuranProgress: true,
    includeNotes: true
  })

  const [dateRange, setDateRange] = useState('3months') // Default to past 3 months
  const [customStartDate, setCustomStartDate] = useState(null)
  const [customEndDate, setCustomEndDate] = useState(null)

  const [printData, setPrintData] = useState({
    attendance: [],
    quranProgress: [],
    notes: [],
    loading: true,
    error: null
  })

  const studentName = student ? `${student.first_name} ${student.last_name}` : ''

  // Get the current date range option
  const currentDateRangeOption = dateRangeOptions.find(option => option.value === dateRange)

  const fetchPrintData = useCallback(async () => {
    try {
      setPrintData(prev => ({ ...prev, loading: true, error: null }))

      // Calculate date filter based on selected range
      const dateRangeOption = dateRangeOptions.find(option => option.value === dateRange)
      let dateFilter = null
      let endDateFilter = null
      
      if (dateRange === 'custom') {
        // Use custom date range if both dates are selected
        if (customStartDate) {
          dateFilter = dayjs(customStartDate).format('YYYY-MM-DD')
        }
        if (customEndDate) {
          endDateFilter = dayjs(customEndDate).format('YYYY-MM-DD')
        }
      } else if (dateRangeOption && dateRangeOption.days) {
        dateFilter = dayjs().subtract(dateRangeOption.days, 'day').format('YYYY-MM-DD')
      }

      // Build attendance query
      let attendanceQuery = supabase
        .from('attendance')
        .select(`
          date,
          status,
          class_id,
          classes(name)
        `)
        .eq('student_id', student.id)
        .order('date', { ascending: false })

      // Apply date filter if not "all time"
      if (dateFilter) {
        attendanceQuery = attendanceQuery.gte('date', dateFilter)
      }
      if (endDateFilter) {
        attendanceQuery = attendanceQuery.lte('date', endDateFilter)
      }

      // Add reasonable limits based on date range
      const getLimit = (baseLimit) => {
        if (dateRange === 'all') return baseLimit * 3 // Higher limit for all time
        if (dateRange === 'year') return baseLimit * 2 // Higher limit for year
        return baseLimit
      }

      attendanceQuery = attendanceQuery.limit(getLimit(200))

      // Build Quran progress query
      let quranProgressQuery = supabase
        .from('student_quran_progress')
        .select(`
          date,
          starting_surah,
          starting_verse,
          ending_surah,
          ending_verse,
          class_id,
          classes(name)
        `)
        .eq('student_id', student.id)
        .order('date', { ascending: false })

      if (dateFilter) {
        quranProgressQuery = quranProgressQuery.gte('date', dateFilter)
      }
      if (endDateFilter) {
        quranProgressQuery = quranProgressQuery.lte('date', endDateFilter)
      }
      quranProgressQuery = quranProgressQuery.limit(getLimit(100))

      // Build notes query with class information
      let notesQuery = supabase
        .from('student_notes')
        .select(`
          created_at,
          description,
          type,
          class_id,
          classes(name)
        `)
        .eq('student_id', student.id)
        .order('created_at', { ascending: false })

      // For notes, use timestamp filtering if date filter exists
      if (dateFilter) {
        // Convert date to start of day timestamp for proper filtering
        const startOfDay = dayjs(dateFilter).startOf('day').toISOString()
        notesQuery = notesQuery.gte('created_at', startOfDay)
      }
      if (endDateFilter) {
        // Convert date to end of day timestamp for proper filtering
        const endOfDay = dayjs(endDateFilter).endOf('day').toISOString()
        notesQuery = notesQuery.lte('created_at', endOfDay)
      }
      notesQuery = notesQuery.limit(getLimit(50))

      const [attendanceResult, quranProgressResult, notesResult] = await Promise.allSettled([
        attendanceQuery,
        quranProgressQuery,
        notesQuery
      ])

      setPrintData({
        attendance: attendanceResult.status === 'fulfilled' ? (attendanceResult.value.data || []) : [],
        quranProgress: quranProgressResult.status === 'fulfilled' ? (quranProgressResult.value.data || []) : [],
        notes: notesResult.status === 'fulfilled' ? (notesResult.value.data || []) : [],
        loading: false,
        error: null
      })
    } catch (error) {
      console.error('Error fetching print data:', error)
      setPrintData(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load print data. Please try again.'
      }))
    }
  }, [student, dateRange, customStartDate, customEndDate])

  // Fetch data for printing when dialog opens or date range changes
  useEffect(() => {
    if (open && student) {
      fetchPrintData()
    }
  }, [open, student, dateRange, fetchPrintData])

  const handlePrint = () => {
    window.print()
  }

  const handleOptionChange = (option) => {
    setPrintOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }))
  }

  const handleClearDateFilter = () => {
    setDateRange('month') // Reset to default "Past Month"
    setCustomStartDate(null)
    setCustomEndDate(null)
  }


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        className: 'print-container',
        sx: { 
          minHeight: { xs: '100vh', sm: '90vh' },
          height: { xs: '100vh', sm: '90vh' },
          margin: { xs: 0, sm: 2 },
          borderRadius: { xs: 0, sm: 2 },
        }
      }}
    >
      <DialogTitle className="no-print" sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
            Print Student Report
          </Typography>
          <IconButton onClick={onClose} size={isMobile ? 'small' : 'medium'}>
            <CloseIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className="no-print" sx={{ 
        p: 0, 
        height: { xs: 'calc(100% - 80px)', sm: 'calc(100% - 120px)' }, 
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
      }}>
        {/* Left Panel - Options */}
        <Box sx={{ 
          width: { xs: '100%', sm: '350px' },
          minWidth: { xs: '100%', sm: '350px' },
          borderRight: { xs: 'none', sm: '1px solid #ddd' },
          borderBottom: { xs: '1px solid #ddd', sm: 'none' },
          p: { xs: 2, sm: 3 },
          backgroundColor: 'background.paper',
          overflow: 'auto',
          maxHeight: { xs: '40vh', sm: 'none' },
        }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
            Print Options:
          </Typography>
          <FormGroup sx={{ '& .MuiFormControlLabel-root': { mb: { xs: 1, sm: 1.5 } } }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={printOptions.includeAttendance}
                  onChange={() => handleOptionChange('includeAttendance')}
                  size="small"
                />
              }
              label="Attendance Records"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: { xs: '0.875rem', sm: '0.95rem' } } }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={printOptions.includeQuranProgress}
                  onChange={() => handleOptionChange('includeQuranProgress')}
                  size="small"
                />
              }
              label="Quran Progress"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: { xs: '0.875rem', sm: '0.95rem' } } }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={printOptions.includeNotes}
                  onChange={() => handleOptionChange('includeNotes')}
                  size="small"
                />
              }
              label="Notes"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: { xs: '0.875rem', sm: '0.95rem' } } }}
            />
          </FormGroup>
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Basic Information is always included
          </Typography>
          
          <Divider sx={{ my: { xs: 2, sm: 3 } }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: { xs: 1, sm: 0 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
              Date Range:
            </Typography>
            <Button
              size="small"
              startIcon={<ClearIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
              onClick={handleClearDateFilter}
              sx={{ 
                minWidth: 'auto',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                textTransform: 'none',
                padding: { xs: '4px 8px', sm: '6px 12px' },
              }}
            >
              Clear
            </Button>
          </Box>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Time Period</InputLabel>
            <Select
              value={dateRange}
              label="Time Period"
              onChange={(e) => setDateRange(e.target.value)}
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '& .MuiSelect-select': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            >
              {dateRangeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Custom Date Range Pickers */}
          {dateRange === 'custom' && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Start Date"
                      value={customStartDate}
                      onChange={(newValue) => setCustomStartDate(newValue)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          sx: {
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                            },
                          },
                        }
                      }}
                      maxDate={customEndDate || dayjs()}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="End Date"
                      value={customEndDate}
                      onChange={(newValue) => setCustomEndDate(newValue)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          sx: {
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                            },
                          },
                        }
                      }}
                      minDate={customStartDate}
                      maxDate={dayjs()}
                    />
                  </Grid>
                </Grid>
              </Box>
            </LocalizationProvider>
          )}
          
          {/* Date Range Preview */}
          {currentDateRangeOption && (
            <Box sx={{ 
              p: { xs: 1.5, sm: 2 }, 
              backgroundColor: 'background.default', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500, mb: 0.5, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                Date Range Preview:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                {dateRange === 'all' 
                  ? 'Shows all historical data for this student'
                  : dateRange === 'custom'
                    ? (customStartDate && customEndDate 
                      ? `${dayjs(customStartDate).format('MMM D, YYYY')} to ${dayjs(customEndDate).format('MMM D, YYYY')}`
                      : customStartDate 
                        ? `From ${dayjs(customStartDate).format('MMM D, YYYY')} onwards`
                        : customEndDate
                          ? `Up to ${dayjs(customEndDate).format('MMM D, YYYY')}`
                          : 'Please select start and/or end dates'
                    )
                    : `${dayjs().subtract(currentDateRangeOption.days, 'day').format('MMM D, YYYY')} to ${dayjs().format('MMM D, YYYY')}`
                }
              </Typography>
            </Box>
          )}

          {printData.loading && (
            <Alert severity="info" sx={{ mt: 2, fontSize: { xs: '0.875rem', sm: '1rem' }, '& .MuiAlert-message': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}>
              Loading print data...
            </Alert>
          )}

          {printData.error && (
            <Alert severity="error" sx={{ mt: 2, fontSize: { xs: '0.875rem', sm: '1rem' }, '& .MuiAlert-message': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}>
              {printData.error}
            </Alert>
          )}

          {/* Action Buttons */}
          <Box sx={{ mt: { xs: 3, sm: 4 } }}>
            <Button
              onClick={handlePrint}
              variant="contained"
              startIcon={<PrintIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
              disabled={printData.loading}
              fullWidth
              size="small"
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' },
                padding: { xs: '8px 16px', sm: '6px 16px' },
              }}
            >
              Print Report
            </Button>
          </Box>
        </Box>

        {/* Right Panel - Print Preview */}
        <Box sx={{ 
          flex: 1,
          overflow: 'auto',
          backgroundColor: '#f5f5f5',
          p: { xs: 1, sm: 2 },
          minHeight: { xs: '60vh', sm: 'auto' },
        }}>
          <Paper sx={{ 
            minHeight: '100%',
            p: { xs: 2, sm: 3 },
            backgroundColor: 'white',
            boxShadow: 2
          }} className="print-content">
            {/* Print Header */}
            <Box className="print-only print-header">
              <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                Student Detail Report
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Generated on {dayjs().format('MMMM D, YYYY [at] h:mm A')}
              </Typography>
              <Typography variant="subtitle2" color="text.primary" sx={{ fontWeight: 500, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                Date Range: {
                  dateRange === 'all' 
                    ? 'All Time'
                    : dateRange === 'custom'
                      ? (customStartDate && customEndDate 
                        ? `${dayjs(customStartDate).format('MMM D, YYYY')} to ${dayjs(customEndDate).format('MMM D, YYYY')}`
                        : customStartDate 
                          ? `From ${dayjs(customStartDate).format('MMM D, YYYY')} onwards`
                          : customEndDate
                            ? `Up to ${dayjs(customEndDate).format('MMM D, YYYY')}`
                            : 'Custom Range (No dates selected)'
                      )
                      : currentDateRangeOption?.label || 'Unknown Range'
                }
              </Typography>
              <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />
            </Box>

            {/* Basic Information - Always included */}
            {student && (
              <Box sx={{ mb: { xs: 3, sm: 4 } }} className="student-info-grid">
                <Typography variant="h5" gutterBottom className="print-section-header" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}>
                  Student Information
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}>
                  {studentName}
                </Typography>
                <Grid container spacing={1} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={4} className="student-info-item">
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Date of Birth:</Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{student.date_of_birth || 'Not provided'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4} className="student-info-item">
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Gender:</Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{student.gender || 'Not provided'}</Typography>
                  </Grid>
                  {guardian && (
                    <Grid item xs={12} sm={4} className="student-info-item">
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Guardian:</Typography>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{guardian.first_name} {guardian.last_name}</Typography>
                    </Grid>
                  )}
                  {guardian && (
                    <>
                      <Grid item xs={12} sm={4} className="student-info-item">
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Guardian Phone:</Typography>
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{guardian.phone || 'Not provided'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4} className="student-info-item">
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Guardian Email:</Typography>
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{guardian.email || 'Not provided'}</Typography>
                      </Grid>
                    </>
                  )}
                  {classes && classes.length > 0 && (
                    <Grid item xs={12} sm={guardian ? 4 : 12} className="student-info-item">
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Classes:</Typography>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{classes.map(cls => cls.name).join(', ')}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* Attendance Summary */}
            {printOptions.includeAttendance && printData.attendance && printData.attendance.length > 0 && (
              <Box sx={{ mb: { xs: 3, sm: 4 } }} className="print-section-break">
                <Typography variant="h5" gutterBottom className="print-section-header" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}>
                  Attendance Records
                </Typography>
                <TableContainer 
                  component={Paper}
                  sx={{
                    overflowX: { xs: 'auto', sm: 'visible' },
                    [theme.breakpoints.down('sm')]: {
                      '&::-webkit-scrollbar': {
                        height: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '4px',
                      },
                    },
                  }}
                >
                  <Table size="small" sx={{ minWidth: { xs: 500, sm: 'auto' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600 }}><strong>Date</strong></TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600 }}><strong>Day</strong></TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600 }}><strong>Status</strong></TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600 }}><strong>Class</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {printData.attendance.map((record, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>{dayjs(record.date).format('MMM D, YYYY')}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>{dayjs(record.date).format('dddd')}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>{record.status}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>{record.classes?.name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Showing {printData.attendance.length} attendance records for {currentDateRangeOption?.label.toLowerCase() || 'all time'}
                </Typography>
              </Box>
            )}

            {/* Quran Progress */}
            {printOptions.includeQuranProgress && printData.quranProgress && printData.quranProgress.length > 0 && (
              <Box sx={{ mb: { xs: 3, sm: 4 } }} className="print-section-break">
                <Typography variant="h5" gutterBottom className="print-section-header" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}>
                  Quran Progress
                </Typography>
                <TableContainer 
                  component={Paper}
                  sx={{
                    overflowX: { xs: 'auto', sm: 'visible' },
                    [theme.breakpoints.down('sm')]: {
                      '&::-webkit-scrollbar': {
                        height: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '4px',
                      },
                    },
                  }}
                >
                  <Table size="small" sx={{ minWidth: { xs: 600, sm: 'auto' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600 }}><strong>Date</strong></TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600 }}><strong>From</strong></TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600 }}><strong>To</strong></TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600 }}><strong>Class</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {printData.quranProgress.map((record, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>{dayjs(record.date).format('MMM D, YYYY')}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                            Surah {record.starting_surah}, Verse {record.starting_verse}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                            Surah {record.ending_surah}, Verse {record.ending_verse}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>{record.classes?.name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Showing {printData.quranProgress.length} progress records for {currentDateRangeOption?.label.toLowerCase() || 'all time'}
                </Typography>
              </Box>
            )}

            {/* Notes */}
            {printOptions.includeNotes && printData.notes && printData.notes.length > 0 && (
              <Box sx={{ mb: { xs: 3, sm: 4 } }} className="print-section-break">
                <Typography variant="h5" gutterBottom className="print-section-header" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}>
                  Student Notes
                </Typography>
                <TableContainer 
                  component={Paper}
                  sx={{
                    overflowX: { xs: 'auto', sm: 'visible' },
                    [theme.breakpoints.down('sm')]: {
                      '&::-webkit-scrollbar': {
                        height: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '4px',
                      },
                    },
                  }}
                >
                  <Table size="small" sx={{ minWidth: { xs: 600, sm: 'auto' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600 }}><strong>Date</strong></TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600 }}><strong>Type</strong></TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600 }}><strong>Note</strong></TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600 }}><strong>Class</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {printData.notes.map((note, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>{dayjs(note.created_at).format('MMM D, YYYY')}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>{note.type || 'General'}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>{note.description}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>{note.classes?.name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Showing {printData.notes.length} notes for {currentDateRangeOption?.label.toLowerCase() || 'all time'}
                </Typography>
              </Box>
            )}

            {/* Empty state messages */}
            {printOptions.includeAttendance && printData.attendance.length === 0 && !printData.loading && (
              <Box sx={{ mb: { xs: 3, sm: 4 } }}>
                <Typography variant="h5" gutterBottom className="print-section-header" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}>
                  Attendance Records
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  No attendance records found for this student in the selected time period.
                </Typography>
              </Box>
            )}

            {printOptions.includeQuranProgress && printData.quranProgress.length === 0 && !printData.loading && (
              <Box sx={{ mb: { xs: 3, sm: 4 } }}>
                <Typography variant="h5" gutterBottom className="print-section-header" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}>
                  Quran Progress
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  No Quran progress records found for this student in the selected time period.
                </Typography>
              </Box>
            )}

            {printOptions.includeNotes && printData.notes.length === 0 && !printData.loading && (
              <Box sx={{ mb: { xs: 3, sm: 4 } }}>
                <Typography variant="h5" gutterBottom className="print-section-header" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}>
                  Student Notes
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  No notes found for this student in the selected time period.
                </Typography>
              </Box>
            )}

            {/* Print Footer */}
            <Box className="print-only print-footer">
              <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                End of Report - Generated by DMS System on {dayjs().format('MMMM D, YYYY [at] h:mm A')}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      {/* Hidden print-only content */}
      <div className="print-only-content" style={{ display: 'none' }}>
        <div style={{ padding: '0 20px 0 20px', backgroundColor: 'white', color: 'black' }}>
          {/* Print Header */}
          <div style={{ textAlign: 'center', marginBottom: '0.4cm', paddingBottom: '0.2cm', borderBottom: '1px solid #3498db' }}>
            <h1 style={{ fontSize: '18pt', margin: '0 0 5px 0', fontWeight: 'bold' }}>
              Student Detail Report
            </h1>
            <p style={{ fontSize: '10pt', margin: '0 0 3px 0', color: '#666' }}>
              Generated on {dayjs().format('MMM D, YYYY [at] h:mm A')} | Date Range: {
                dateRange === 'all' 
                  ? 'All Time'
                  : dateRange === 'custom'
                    ? (customStartDate && customEndDate 
                      ? `${dayjs(customStartDate).format('MMM D, YYYY')} to ${dayjs(customEndDate).format('MMM D, YYYY')}`
                      : customStartDate 
                        ? `From ${dayjs(customStartDate).format('MMM D, YYYY')} onwards`
                        : customEndDate
                          ? `Up to ${dayjs(customEndDate).format('MMM D, YYYY')}`
                          : 'Custom Range (No dates selected)'
                    )
                    : currentDateRangeOption?.label || 'Unknown Range'
              }
            </p>
          </div>

          {/* Basic Information - Always included */}
          {student && (
            <div style={{ marginBottom: '0.5cm' }}>
              <h2 style={{ fontSize: '18pt', fontWeight: 'bold', marginBottom: '0.5cm', borderBottom: '2px solid #3498db', paddingBottom: '0.2cm' }}>
                Student Information
              </h2>
              <h3 style={{ fontSize: '16pt', fontWeight: '600', marginBottom: '0.5cm' }}>
                {studentName}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5cm', marginBottom: '0.5cm' }}>
                <div>
                  <strong>Date of Birth:</strong><br />
                  {student.date_of_birth || 'Not provided'}
                </div>
                <div>
                  <strong>Gender:</strong><br />
                  {student.gender || 'Not provided'}
                </div>
                {guardian && (
                  <div>
                    <strong>Guardian:</strong><br />
                    {guardian.first_name} {guardian.last_name}
                  </div>
                )}
              </div>
              {guardian && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5cm', marginBottom: '0.5cm' }}>
                  <div>
                    <strong>Guardian Phone:</strong><br />
                    {guardian.phone || 'Not provided'}
                  </div>
                  <div>
                    <strong>Guardian Email:</strong><br />
                    {guardian.email || 'Not provided'}
                  </div>
                  {classes && classes.length > 0 && (
                    <div>
                      <strong>Classes:</strong><br />
                      {classes.map(cls => cls.name).join(', ')}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Attendance Summary */}
          {printOptions.includeAttendance && printData.attendance && printData.attendance.length > 0 && (
            <div style={{ marginBottom: '0.5cm' }}>
              <h2 style={{ fontSize: '18pt', fontWeight: 'bold', marginBottom: '0.5cm', borderBottom: '2px solid #3498db', paddingBottom: '0.2cm' }}>
                Attendance Records
              </h2>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10pt' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #ddd', padding: '6px', backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Date</th>
                    <th style={{ border: '1px solid #ddd', padding: '6px', backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Day</th>
                    <th style={{ border: '1px solid #ddd', padding: '6px', backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Status</th>
                    <th style={{ border: '1px solid #ddd', padding: '6px', backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Class</th>
                  </tr>
                </thead>
                <tbody>
                  {printData.attendance.map((record, index) => (
                    <tr key={index}>
                      <td style={{ border: '1px solid #ddd', padding: '6px' }}>{dayjs(record.date).format('MMM D, YYYY')}</td>
                      <td style={{ border: '1px solid #ddd', padding: '6px' }}>{dayjs(record.date).format('dddd')}</td>
                      <td style={{ border: '1px solid #ddd', padding: '6px' }}>{record.status}</td>
                      <td style={{ border: '1px solid #ddd', padding: '6px' }}>{record.classes?.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: '10pt', color: '#666', marginTop: '0.3cm' }}>
                Showing {printData.attendance.length} attendance records for {currentDateRangeOption?.label.toLowerCase() || 'all time'}
              </p>
            </div>
          )}

          {/* Quran Progress */}
          {printOptions.includeQuranProgress && printData.quranProgress && printData.quranProgress.length > 0 && (
            <div style={{ marginBottom: '0.5cm' }}>
              <h2 style={{ fontSize: '18pt', fontWeight: 'bold', marginBottom: '0.5cm', borderBottom: '2px solid #3498db', paddingBottom: '0.2cm' }}>
                Quran Progress
              </h2>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10pt' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #ddd', padding: '6px', backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Date</th>
                    <th style={{ border: '1px solid #ddd', padding: '6px', backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>From</th>
                    <th style={{ border: '1px solid #ddd', padding: '6px', backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>To</th>
                    <th style={{ border: '1px solid #ddd', padding: '6px', backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Class</th>
                  </tr>
                </thead>
                <tbody>
                  {printData.quranProgress.map((record, index) => (
                    <tr key={index}>
                      <td style={{ border: '1px solid #ddd', padding: '6px' }}>{dayjs(record.date).format('MMM D, YYYY')}</td>
                      <td style={{ border: '1px solid #ddd', padding: '6px' }}>
                        Surah {record.starting_surah}, Verse {record.starting_verse}
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '6px' }}>
                        Surah {record.ending_surah}, Verse {record.ending_verse}
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '6px' }}>{record.classes?.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: '10pt', color: '#666', marginTop: '0.3cm' }}>
                Showing {printData.quranProgress.length} progress records for {currentDateRangeOption?.label.toLowerCase() || 'all time'}
              </p>
            </div>
          )}

          {/* Notes */}
          {printOptions.includeNotes && printData.notes && printData.notes.length > 0 && (
            <div style={{ marginBottom: '0.5cm' }}>
              <h2 style={{ fontSize: '18pt', fontWeight: 'bold', marginBottom: '0.5cm', borderBottom: '2px solid #3498db', paddingBottom: '0.2cm' }}>
                Student Notes
              </h2>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10pt' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #ddd', padding: '6px', backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Date</th>
                    <th style={{ border: '1px solid #ddd', padding: '6px', backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Type</th>
                    <th style={{ border: '1px solid #ddd', padding: '6px', backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Note</th>
                    <th style={{ border: '1px solid #ddd', padding: '6px', backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Class</th>
                  </tr>
                </thead>
                <tbody>
                  {printData.notes.map((note, index) => (
                    <tr key={index}>
                      <td style={{ border: '1px solid #ddd', padding: '6px' }}>{dayjs(note.created_at).format('MMM D, YYYY')}</td>
                      <td style={{ border: '1px solid #ddd', padding: '6px' }}>{note.type || 'General'}</td>
                      <td style={{ border: '1px solid #ddd', padding: '6px' }}>{note.description}</td>
                      <td style={{ border: '1px solid #ddd', padding: '6px' }}>{note.classes?.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: '10pt', color: '#666', marginTop: '0.3cm' }}>
                Showing {printData.notes.length} notes for {currentDateRangeOption?.label.toLowerCase() || 'all time'}
              </p>
            </div>
          )}

          {/* Empty state messages */}
          {printOptions.includeAttendance && printData.attendance.length === 0 && !printData.loading && (
            <div style={{ marginBottom: '1cm' }}>
              <h2 style={{ fontSize: '18pt', fontWeight: 'bold', marginBottom: '0.5cm', borderBottom: '2px solid #3498db', paddingBottom: '0.2cm' }}>
                Attendance Records
              </h2>
              <p style={{ fontSize: '12pt', color: '#666' }}>
                No attendance records found for this student in the selected time period.
              </p>
            </div>
          )}

          {printOptions.includeQuranProgress && printData.quranProgress.length === 0 && !printData.loading && (
            <div style={{ marginBottom: '1cm' }}>
              <h2 style={{ fontSize: '18pt', fontWeight: 'bold', marginBottom: '0.5cm', borderBottom: '2px solid #3498db', paddingBottom: '0.2cm' }}>
                Quran Progress
              </h2>
              <p style={{ fontSize: '12pt', color: '#666' }}>
                No Quran progress records found for this student in the selected time period.
              </p>
            </div>
          )}

          {printOptions.includeNotes && printData.notes.length === 0 && !printData.loading && (
            <div style={{ marginBottom: '1cm' }}>
              <h2 style={{ fontSize: '18pt', fontWeight: 'bold', marginBottom: '0.5cm', borderBottom: '2px solid #3498db', paddingBottom: '0.2cm' }}>
                Student Notes
              </h2>
              <p style={{ fontSize: '12pt', color: '#666' }}>
                No notes found for this student in the selected time period.
              </p>
            </div>
          )}

        </div>
      </div>
    </Dialog>
  )
}

export default StudentPrintView
